/**
 * Video Generation API Client
 * Integrates with the Foodie Xanadu Video API for business video generation
 */

import { authService } from './authService';
import { buildOrgScopedVideoPath, withOrgHeader } from './apiContext';

// Base URL with default fallback
const API_BASE_URL = import.meta.env.VITE_VIDEO_API_BASE_URL || 'http://localhost:5001';
const COMPLETED_VIDEO_STATES = new Set(['completed', 'done', 'ready', 'success', 'succeeded']);

const normalizeMediaUrl = (baseUrl, url) => {
  if (typeof url !== 'string' || !url) return '';
  if (
    url.startsWith('http://')
    || url.startsWith('https://')
    || url.startsWith('blob:')
    || url.startsWith('data:')
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }
  return url;
};

const normalizeVideoAssets = (baseUrl, jobId, state, completedAt, assets = {}) => {
  const normalizedAssets = { ...(assets || {}) };
  const isCompleted =
    COMPLETED_VIDEO_STATES.has(String(state || '').toLowerCase())
    || Boolean(completedAt);

  normalizedAssets.video_url = normalizeMediaUrl(baseUrl, normalizedAssets.video_url);
  normalizedAssets.thumbnail_url = normalizeMediaUrl(baseUrl, normalizedAssets.thumbnail_url);

  if (!normalizedAssets.video_url && jobId && isCompleted) {
    normalizedAssets.video_url = `${baseUrl}${buildOrgScopedVideoPath(jobId, '/asset')}`;
  }
  if (!normalizedAssets.thumbnail_url && jobId && isCompleted) {
    normalizedAssets.thumbnail_url = `${baseUrl}${buildOrgScopedVideoPath(jobId, '/thumbnail')}`;
  }

  return normalizedAssets;
};

/**
 * Custom error class for Video API errors
 */
class VideoApiError extends Error {
  constructor(message, statusCode = 0, details = null) {
    super(message);
    this.name = 'VideoApiError';
    this.statusCode = statusCode;
    this.details = details;
  }

  get isNetworkError() {
    return this.statusCode === 0;
  }

  get isClientError() {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  get isServerError() {
    return this.statusCode >= 500;
  }
}

/**
 * Video API Client Class
 */
class VideoApiClient {
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || API_BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Centralized request handler with authentication
   */
  async _makeRequest(path, options = {}, { includeOrg = true, asBlob = false } = {}) {
    const url = `${this.baseUrl}${path}`;
    const headers = withOrgHeader(
      { ...this.headers, ...(options.headers || {}) },
      { includeOrg },
    );

    try {
      const response = await fetch(url, {
        credentials: 'include', // Required for session cookies
        ...options,
        headers,
      });

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        console.warn('Unauthorized - redirecting to login');
        authService.handleUnauthorized();
        throw new VideoApiError('Unauthorized', 401, { redirect: true });
      }

      if (!response.ok) {
        if (response.status === 404) {
          throw new VideoApiError('Resource not found', 404);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new VideoApiError(
          errorData.detail || errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      return asBlob ? await response.blob() : await response.json();
    } catch (error) {
      if (error instanceof VideoApiError) {
        throw error;
      }
      throw new VideoApiError(`Network error: ${error.message}`, 0, error);
    }
  }

  /**
   * Create a video generation job
   * @param {Object} params - Video generation parameters
   * @param {Object} params.source - Source information
   * @param {string} [params.source.place_id] - Google Place ID
   * @param {string} [params.source.maps_url] - Google Maps URL
   * @param {Object} [params.campaign] - Campaign configuration
   * @param {Object} [params.metadata] - Additional metadata
   * @param {Object} [params.video_config_overrides] - Video configuration overrides
   * @param {string} [params.video_config_overrides.aspect_ratio] - Aspect ratio ('16:9' for horizontal, '9:16' for vertical)
   * @param {number} [params.video_config_overrides.length] - Video length in seconds (6, 15, or 30)
   * @param {string} [params.video_config_overrides.script_mode] - Scripting mode ('creative' or 'grounded')
   * @param {string} [params.video_config_overrides.voice_id] - Narration voice id (e.g. 'female-000'); null for automatic casting
   * @param {boolean} [params.video_config_overrides.logo_overlay_enabled] - Enable logo overlay
   * @param {string} [params.video_config_overrides.logo_overlay_position] - Logo position (e.g., 'bottom-right', 'top-left', 'auto')
   * @param {number} [params.video_config_overrides.logo_overlay_opacity] - Logo opacity (0.0 to 1.0)
   * @param {Object} [options] - Request options
   * @param {string} [options.idempotencyKey] - Unique key for request deduplication
   * @returns {Promise<Object>} API response with job_id and status_url
   */
  async createVideoJob({
    source,
    campaign = {
      objective: 'brand_awareness',
      distribution_channel: 'CTV'
    },
    metadata = {
      requested_by: 'cornercast-frontend',
      locale: 'en-US'
    },
    video_config_overrides = null
  }, options = {}) {
    // Validate required parameters
    if (!source || (!source.place_id && !source.maps_url)) {
      throw new Error('Either place_id or maps_url must be provided in source');
    }

    const requestBody = {
      source: {
        place_id: source.place_id || null,
        maps_url: source.maps_url || null
      },
      campaign,
      metadata
    };

    // Add video_config_overrides if provided
    if (video_config_overrides) {
      requestBody.video_config_overrides = video_config_overrides;
    }

    const headers = { ...this.headers };

    // Add idempotency key if provided
    if (options.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }

    const data = await this._makeRequest('/api/v1/videos', {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    return {
      jobId: data.job_id,
      status: data.status,
      statusUrl: data.status_url
    };
  }

  /**
   * Get video job status
   * @param {string} jobId - Job ID to check
   * @returns {Promise<Object>} Job status and details including brand_identity
   */
  async getJobStatus(jobId) {
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    const data = await this._makeRequest(`/api/v1/videos/${jobId}`);
    const resolvedJobId = data.job_id || data.jobId || jobId;
    const completedAt = data.completed_at || data.completedAt;
    const assets = normalizeVideoAssets(
      this.baseUrl,
      resolvedJobId,
      data.state,
      completedAt,
      data.assets,
    );

    // Extract brand_identity from business if available
    const business = data.business || {};

    return {
      jobId: resolvedJobId,
      state: data.state,
      progress: data.progress,
      business: {
        name: business.name,
        address: business.address,
        heroImageUrl: business.hero_image_url,
        brandIdentity: business.brand_identity ? {
          logoUrl: business.brand_identity.logo_url,
          primaryColors: business.brand_identity.primary_colors || [],
          secondaryColors: business.brand_identity.secondary_colors || [],
          fonts: business.brand_identity.fonts || [],
          mood: business.brand_identity.mood,
          moodKeywords: business.brand_identity.mood_keywords || []
        } : null
      },
      assets,
      errors: data.errors || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      completedAt,
      videoUrl: assets.video_url || normalizeMediaUrl(this.baseUrl, data.video_url),
      thumbnailUrl: assets.thumbnail_url || normalizeMediaUrl(this.baseUrl, data.thumbnail_url),
    };
  }

  /**
   * List video assets with pagination, filtering, and sorting
   * @param {Object} [options] - Query options
   * @param {number} [options.page=1] - Page number (1-indexed)
   * @param {number} [options.pageSize=20] - Number of items per page
   * @param {string} [options.state] - Filter by job state (e.g., 'completed', 'processing')
   * @param {string} [options.sort='updated_at:desc'] - Sort field and order (e.g., 'created_at:asc')
   * @param {AbortSignal} [options.signal] - Aborts the request (e.g. when a newer page is requested)
   * @returns {Promise<Object>} Paginated response with assets array and metadata
   */
  async listVideoAssets(options = {}) {
    const {
      page = 1,
      pageSize = 20,
      state,
      sort = 'updated_at:desc',
      organizationId,
      advertiserId,
      signal
    } = options;

    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (state) params.append('state', state);
    if (sort) params.append('sort', sort);
    if (organizationId) params.append('organization_id', organizationId);
    if (advertiserId) params.append('advertiser_id', advertiserId);

    const data = await this._makeRequest(`/api/v1/videos/assets?${params.toString()}`, { signal });

    return {
      assets: data.assets || [],
      total: data.total || 0,
      page: data.page || page,
      pageSize: data.page_size || pageSize,
      hasNext: data.has_next || false,
      hasPrev: data.has_prev || false
    };
  }

  /**
   * Save the team's review note on a video. There is one shared note per
   * video (last save wins); an empty note clears it.
   * @param {string} jobId - Job ID
   * @param {string} feedback - Free-text note
   * @returns {Promise<Object|null>} Saved note ({ feedback, updated_at,
   *   updated_by_name, updated_by_email }), or null once cleared
   */
  async saveVideoFeedback(jobId, feedback) {
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    return this._makeRequest(`/api/v1/videos/${jobId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify({ feedback }),
    });
  }

  /**
   * Download every review note in an organization as CSV (job_id, feedback,
   * business_name, updated_by, updated_at).
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Blob>} The CSV file
   */
  async exportVideoFeedbackCsv(organizationId) {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this._makeRequest(
      `/api/v1/videos/feedback/export?${params.toString()}`,
      {},
      { asBlob: true },
    );
  }

  /**
   * Get thumbnail URL for a video job. Uses the organization-scoped path so
   * the URL can be safely placed on a native <img> (which cannot send the
   * X-Cornercast-Organization-Id header).
   * @param {string} jobId - Job ID
   * @returns {string} Full URL to the thumbnail endpoint
   */
  getThumbnailUrl(jobId) {
    if (!jobId) return null;
    return `${this.baseUrl}${buildOrgScopedVideoPath(jobId, '/thumbnail')}`;
  }

  /**
   * Get asset URL for a rendered video job. Uses the organization-scoped path
   * so the URL can be safely placed on a native <video> element.
   * @param {string} jobId - Job ID
   * @returns {string} Full URL to the asset endpoint
   */
  getAssetUrl(jobId) {
    if (!jobId) return null;
    return `${this.baseUrl}${buildOrgScopedVideoPath(jobId, '/asset')}`;
  }

  /**
   * Retry/resume a failed or pending video job
   * @param {string} jobId - Job ID to retry
   * @returns {Promise<Object>} Latest job status after retry is queued
   */
  async retryVideoJob(jobId) {
    if (!jobId) {
      throw new Error('Job ID is required to retry a video');
    }

    return this._makeRequest(`/api/v1/videos/${jobId}/retry`, {
      method: 'POST',
    });
  }

  /**
   * Restart video generation from existing project data
   * @param {string} jobId - Job ID to restart
   * @returns {Promise<Object>} Latest job status after restart is queued
   */
  async restartVideoJob(jobId) {
    if (!jobId) {
      throw new Error('Job ID is required to restart a video');
    }

    return this._makeRequest(`/api/v1/videos/${jobId}/restart`, {
      method: 'POST',
    });
  }

  /**
   * Create a Stripe Checkout Session for a creative-generation job.
   * Generation does NOT start here — it begins only after the back-end
   * confirms payment (webhook or confirmPayment). The price is controlled
   * entirely server-side.
   * @param {string} jobId - The already-created creative job to pay for
   * @param {Object} params
   * @param {string} params.successUrl - Absolute success return URL (app origin; may include {CHECKOUT_SESSION_ID})
   * @param {string} params.cancelUrl - Absolute cancel return URL (app origin)
   * @param {string} [params.customerEmail] - Pre-fill the Stripe email field
   * @returns {Promise<{jobId: string, sessionId: string, checkoutUrl: string, paymentStatus: string}>}
   */
  async createCheckoutSession(jobId, { successUrl, cancelUrl, customerEmail, fbp, fbc } = {}) {
    if (!jobId) {
      throw new Error('Job ID is required to start checkout');
    }

    const data = await this._makeRequest(`/api/v1/videos/${jobId}/checkout-session`, {
      method: 'POST',
      body: JSON.stringify({
        success_url: successUrl,
        cancel_url: cancelUrl,
        customer_email: customerEmail || null,
        fbp: fbp || null,
        fbc: fbc || null,
      }),
    });

    return {
      jobId: data.job_id,
      sessionId: data.session_id,
      checkoutUrl: data.checkout_url,
      paymentStatus: data.payment_status,
    };
  }

  /**
   * Securely confirm a Checkout Session and start generation if Stripe reports
   * the payment as paid. The back-end re-fetches the session from Stripe — it
   * never trusts the success redirect alone. Idempotent.
   * @param {string} jobId
   * @param {string} sessionId - Stripe Checkout Session id from the success URL
   * @returns {Promise<{jobId: string, paid: boolean, generationStarted: boolean, paymentStatus: string, jobState: string}>}
   */
  async confirmPayment(jobId, sessionId) {
    if (!jobId || !sessionId) {
      throw new Error('Job ID and session ID are required to confirm payment');
    }

    const data = await this._makeRequest(`/api/v1/videos/${jobId}/confirm-payment`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });

    return {
      jobId: data.job_id,
      paid: data.paid,
      generationStarted: data.generation_started,
      paymentStatus: data.payment_status,
      jobState: data.job_state,
    };
  }

  /**
   * Submit a "Launch on CTV" follow-up request. Sends the lead to the
   * CornerCast team (Google Chat) via the back-end; does not launch anything.
   * @param {Object} lead - { email, name, business_name, maps_or_website,
   *   creative_id, creative_url, location, budget, timing, notes }
   * @returns {Promise<{received: boolean, notified: boolean}>}
   */
  async submitCtvLead(lead) {
    return this._makeRequest('/api/v1/leads/ctv', {
      method: 'POST',
      body: JSON.stringify(lead),
    });
  }

  /**
   * Submit in-app feedback / open a case. Sends a message to the CornerCast
   * team (Google Chat) via the back-end.
   * @param {Object} feedback - { message, category, email, page_url }
   * @returns {Promise<{received: boolean, notified: boolean}>}
   */
  async submitFeedback(feedback) {
    return this._makeRequest('/api/v1/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  }

  /**
   * List narration voices for the voice picker. Each voice includes a hosted
   * preview sample URL.
   * @returns {Promise<{voices: Array<{id: string, name: string, gender: string, sample_url: string}>}>}
   */
  async listVoices() {
    return this._makeRequest('/api/v1/voices', { method: 'GET' }, { includeOrg: false });
  }

  /**
   * Poll job status until completion or failure
   * @param {string} jobId - Job ID to poll
   * @param {Object} [options] - Polling options
   * @param {number} [options.pollInterval] - Polling interval in ms (default: 10000)
   * @param {function} [options.onProgress] - Progress callback function
   * @returns {Promise<Object>} Final job status
   */
  async pollJobStatus(jobId, options = {}) {
    const {
      pollInterval = 10000, // 10 seconds
      onProgress
    } = options;

    while (true) {
      const status = await this.getJobStatus(jobId);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }

      // Check if job is complete
      if (status.state === 'completed') {
        return status;
      }

      // Check if job failed
      if (status.state === 'failed') {
        throw new VideoApiError(
          `Video generation failed: ${status.errors.map(e => e.message || e).join(', ')}`,
          500,
          { errors: status.errors }
        );
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  /**
   * Get debug information for a video job
   * @param {string} jobId - Job ID to get debug info for
   * @returns {Promise<Object>} Debug information including scene data and project files
   */
  async getJobDebugInfo(jobId) {
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    return this._makeRequest(`/api/v1/videos/${jobId}/debug`);
  }

  /**
   * Get scene media URL (initial frame, final frame, or video)
   * Note: This returns a URL, not a fetch. The URL will include credentials via cookie.
   * @param {string} jobId - Job ID
   * @param {number} sceneIndex - Scene index (0-based)
   * @param {string} kind - Media type: 'initial', 'final', 'video', or 'original'
   * @returns {string} URL for the media resource
   */
  getSceneMediaUrl(jobId, sceneIndex, kind) {
    if (!jobId) {
      throw new Error('Job ID is required');
    }
    if (sceneIndex === undefined || sceneIndex === null) {
      throw new Error('Scene index is required');
    }
    if (!['initial', 'final', 'video', 'original'].includes(kind)) {
      throw new Error('Kind must be one of: initial, final, video, original');
    }

    return `${this.baseUrl}${buildOrgScopedVideoPath(jobId, `/scenes/${sceneIndex}/${kind}`)}`;
  }

  /**
   * Generate video with automatic polling
   * @param {Object} params - Same as createVideoJob
   * @param {Object} [options] - Options for creation and polling
   * @returns {Promise<Object>} Completed job with video assets
   */
  async generateVideo(params, options = {}) {
    // Create the job
    const job = await this.createVideoJob(params, {
      idempotencyKey: options.idempotencyKey
    });

    // Poll until completion
    const result = await this.pollJobStatus(job.jobId, {
      pollInterval: options.pollInterval,
      onProgress: options.onProgress
    });

    return result;
  }
}

/**
 * Job state constants
 */
export const JOB_STATES = {
  ACCEPTED: 'accepted',
  SCRAPING: 'scraping',
  DATA_READY: 'data_ready',
  GENERATING: 'generating',
  RENDERING: 'rendering',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

/**
 * Human-readable state descriptions
 */
export const STATE_DESCRIPTIONS = {
  [JOB_STATES.ACCEPTED]: 'Queued',
  [JOB_STATES.SCRAPING]: 'Collecting venue details',
  [JOB_STATES.DATA_READY]: 'Preparing video workflow',
  [JOB_STATES.GENERATING]: 'Generating content',
  [JOB_STATES.RENDERING]: 'Rendering video',
  [JOB_STATES.COMPLETED]: 'Completed',
  [JOB_STATES.FAILED]: 'Failed'
};

// Export singleton instance
export const videoApiClient = new VideoApiClient();
export { VideoApiClient, VideoApiError };
